import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Empty, ErrorNote } from "../../ui";
import { formatDateTime, formatTime } from "../../../shared/time";
import type { PlanDetail } from "../../../shared/types";
import { listPlans, loadPlan } from "./client";
import { buildSchedule, messageOf, statusTone, type ScheduleCell, type WeekWindow } from "./model";

export function ScheduleBoard({ range, onShift, onToday }: {
  range: WeekWindow;
  onShift: (days: number) => void;
  onToday: () => void;
}) {
  const [plans, setPlans] = useState<PlanDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [truncated, setTruncated] = useState(false);
  const schedule = useMemo(() => buildSchedule(plans), [plans]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError("");
    void listPlans({ status: "active", from: range.from, to: range.to })
      .then(async (summaries) => {
        const limited = summaries.slice(0, 25);
        const settled = await Promise.allSettled(limited.map((plan) => loadPlan(plan.id)));
        if (cancel) return;
        const details = settled.flatMap((result) => result.status === "fulfilled" ? [result.value] : []);
        const failed = settled.filter((result) => result.status === "rejected").length;
        setPlans(details.toSorted((left, right) => left.startsAt.localeCompare(right.startsAt)));
        setTruncated(summaries.length > limited.length);
        if (failed > 0) setError(`${failed} ${failed === 1 ? "plan" : "plans"} in this week could not be loaded.`);
      })
      .catch((caught: unknown) => { if (!cancel) setError(messageOf(caught)); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range.from, range.to]);

  return (
    <div className="stack" aria-busy={loading}>
      <div className="cluster">
        <button className="btn small" type="button" onClick={() => onShift(-7)}>Previous week</button>
        <button className="btn small" type="button" onClick={onToday}>This week</button>
        <button className="btn small" type="button" onClick={() => onShift(7)}>Next week</button>
        <span className="muted">{range.label}</span>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {truncated && <p className="notice">Showing the first 25 plans in this week.</p>}
      {loading && plans.length === 0 && <p className="muted">Loading the week…</p>}
      {!loading && plans.length === 0 && (
        <Empty title="No plans this week" body="Move to another week, or create a plan. Archived plans stay off this grid." />
      )}
      {schedule.conflicts.length > 0 && (
        <article className="panel panel-pad stack">
          <h2>Needs attention</h2>
          <p className="muted">Overlapping plans and blocked times come back with each assignment. There is no separate availability editor.</p>
          {schedule.conflicts.map((cell) => (
            <div key={cell.assignmentId}>
              <Link to={`/services/${cell.planId}?tab=team`}><strong>{cell.personName}</strong></Link>
              <div className="muted">{cell.position} · {cell.team} · {cell.planTitle}</div>
              <ul className="svc-conflicts">
                {cell.conflicts.map((conflict, index) => <li key={`${cell.assignmentId}-${index}`}>{conflict}</li>)}
              </ul>
            </div>
          ))}
        </article>
      )}
      {schedule.openings.length > 0 && (
        <article className="panel panel-pad stack">
          <h2>Open seats</h2>
          {schedule.openings.map((seat) => (
            <Link key={`${seat.planId}-${seat.team}-${seat.position}`} to={`/services/${seat.planId}?tab=team`}>
              <strong>{seat.position}</strong>
              <span className="muted"> · {seat.team} · {seat.planTitle} · {formatTime(seat.startsAt)}</span>
            </Link>
          ))}
        </article>
      )}
      {plans.length > 0 && <ScheduleTable plans={plans} rows={schedule.rows} />}
      {plans.some((plan) => plan.rehearsals.length > 0) && (
        <article className="panel panel-pad stack">
          <h2>Rehearsals</h2>
          {plans.flatMap((plan) => plan.rehearsals.map((rehearsal) => ({ plan, rehearsal })))
            .toSorted((left, right) => left.rehearsal.startsAt.localeCompare(right.rehearsal.startsAt))
            .map(({ plan, rehearsal }) => (
              <Link key={rehearsal.id} to={`/services/${plan.id}?tab=notes`}>
                <strong>{rehearsal.name}</strong>
                <span className="muted"> · {plan.title} · {formatDateTime(rehearsal.startsAt)}{rehearsal.place ? ` · ${rehearsal.place}` : ""}</span>
              </Link>
            ))}
        </article>
      )}
    </div>
  );
}

function ScheduleTable({ plans, rows }: { plans: PlanDetail[]; rows: ReturnType<typeof buildSchedule>["rows"] }) {
  return (
    <>
      <div className="panel table-wrap svc-matrix-scroll">
        <table className="data svc-matrix">
          <caption className="svc-caption">Who is asked to serve. Open a column to assign or respond.</caption>
          <thead>
            <tr>
              <th scope="col">Person</th>
              {plans.map((plan) => (
                <th key={plan.id} scope="col">
                  <Link to={`/services/${plan.id}?tab=team`}>{plan.title}</Link>
                  <div className="faint">{formatTime(plan.startsAt)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={plans.length + 1}>Positions are still open. Nobody has been asked yet.</td></tr>
            )}
            {rows.map((row) => (
              <tr key={row.personId}>
                <th scope="row"><Link to={`/people/${row.personId}`}>{row.personName}</Link></th>
                {plans.map((plan) => (
                  <td key={plan.id}>
                    <Link className="svc-cell-link" to={`/services/${plan.id}?tab=team`}>
                      <AssignmentStack cells={row.cells.filter((cell) => cell.planId === plan.id)} />
                    </Link>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="svc-cards">
        {rows.map((row) => (
          <article className="panel panel-pad stack" key={row.personId}>
            <Link to={`/people/${row.personId}`}><strong>{row.personName}</strong></Link>
            {plans.map((plan) => (
              <Link className="svc-cell-link" key={plan.id} to={`/services/${plan.id}?tab=team`}>
                <span className="faint">{plan.title}</span>
                <AssignmentStack cells={row.cells.filter((cell) => cell.planId === plan.id)} />
              </Link>
            ))}
          </article>
        ))}
      </div>
    </>
  );
}

function AssignmentStack({ cells }: { cells: ScheduleCell[] }) {
  if (cells.length === 0) return <span className="faint">Open</span>;
  return (
    <div className="stack">
      {cells.map((cell) => (
        <div key={cell.assignmentId}>
          <Badge tone={statusTone(cell.status)}>{cell.status}</Badge>
          <div>{cell.position}<span className="faint"> · {cell.team}</span></div>
          {cell.conflicts.map((conflict, index) => <div className="notice" key={`${cell.assignmentId}-${index}`}>{conflict}</div>)}
        </div>
      ))}
    </div>
  );
}
